/* eslint-disable react/jsx-sort-props */

import { styled } from '@abyss/web/tools/styled';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { MAX_COMPARISON_ITEMS, SectionType } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { hideScrollbar } from '../../../../common/ConstantsStyles';
import { DataCardContainer } from '../../../../common/DataCard/DataCardContainer';
import { OutsideCountyAlert } from '../../../../common/DataCard/OutsideCountyAlert';
import { NationalGroupProvidersVirtualDataCard } from '../../../../common/NationalGroupProvidersVirtualBiteCard/NationalGroupProvidersVirtualDataCard';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { CheckBoxDataCardStyle } from '../../../../common/ResultSection/ResultSection.style';
import { isOutOfCounty } from '../../../../common/SnackCardContainer/utils';
import { checkTerminationDate } from '../../../../common/Utils/terminateDateIndicatorUtils';
import { CountySearchContext } from '../../../../context/CountySearchContext';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { Provider } from '../../../../models/Provider';
import { ProviderType } from '../../../../models/ProviderDetails';
import { ResponseHeaders } from '../../../../models/ResponseHeaders';
import { isProviderStartDateInFuture } from '../../../../utils/date.utils';
import { scrollWithRef } from './CompareDrawer/utility/scrollWithRef';
import {
  AddToCompareLabelStyle,
  DataCardCheckBoxStyle,
} from './MapView.styled';

type Props = {
  headers: ResponseHeaders;
  sectionType: string;
  showCheckboxes: boolean;
  providerResults: any[];
  addSelectedProvider: (a: any) => void;
  expandedProviderListView?: boolean;
  updatePin: (a: any) => void;
  selectedCheckbox: any;
  setSelectedCheckbox: (a: any) => void;
  isMobileMap?: boolean;
  DataCardStyle?: Object;
  selectLocation?: (locationId, locationLng, locationLat) => void;
  map?: any;
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  locationForAnalytics?: string;
  pageNumber?: number;
  parentRef?: any;
  selectedFilters?: string;
  searchTerm?: string;
  usedAt?: string;
  navigateToDirections?: boolean;
  setNavigateToDirections?: (a: boolean) => void;
  mobileRouteView?: boolean;
  fromMobileListView: boolean;
  isLoading: boolean;
  isMNR?: boolean;
  enableMapViewEnhancements?: boolean;
  isTieredPlan?: boolean;
  searchRadius?: string | number;
  isGroupedLocationDrawer?: boolean;
  currentPage?: number;
  drawerContent?: any;
  setIsCompareClicked?: () => void;
  compareShareToggle?: boolean;
  recommendationPreferences?: any;
};

export const DataCardListStyled = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  boxSizing: 'border-box',
  alignItems: 'end',
  '@screen < $sm': {
    marginTop: '4px',
  },
  gap: '16px',
  zIndex: '0',
  variants: {
    variant: {
      flexDirectionRow: {
        flexDirection: 'row',
        overflowX: 'auto',
        flexWrap: 'nowrap',
      },
    },
  },
});

export const DataCardList = ({
  headers,
  sectionType,
  showCheckboxes,
  providerResults,
  addSelectedProvider,
  expandedProviderListView,
  updatePin,
  selectedCheckbox,
  setSelectedCheckbox,
  isMobileMap,
  selectLocation,
  map,
  setRouteEndCoords,
  DataCardStyle,
  locationForAnalytics,
  pageNumber,
  parentRef,
  selectedFilters,
  searchTerm,
  usedAt = '',
  navigateToDirections = false,
  setNavigateToDirections,
  mobileRouteView = false,
  fromMobileListView,
  isLoading,
  enableMapViewEnhancements,
  isMNR,
  isTieredPlan,
  searchRadius,
  isGroupedLocationDrawer = false,
  currentPage,
  drawerContent,
  setIsCompareClicked,
  compareShareToggle,
  recommendationPreferences,
}: Props) => {
  const checkedCount = Object.keys(selectedCheckbox.checked).filter(
    (key) => selectedCheckbox.checked[key]
  ).length;

  // begin: a11y keyboard navigation
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const uiState = useStore(StoreKeys.UI_STATE);

  const { compareProvidersFlow, shareProvidersFlow } = useStore(
    StoreKeys.UI_STATE
  );
  const { indexOflastProviderSelected } = compareProvidersFlow;
  const setUIstate = useStore(StoreKeys.SET_UI_STATE);

  const [
    displayTerminationDateEnabled,
    enableMapViewHideOptionFF,
    countySearchAlertFlag,
    enableCompareSmartChoiceFF,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COUNTY_SEARCH_ALERT,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COMPARE_SMART_CHOICE,
  ]);

  const enableCompareSmartChoice = enableCompareSmartChoiceFF && sectionType === SectionType.PROVIDER

  const enableMapViewHideOption: boolean =
    isGroupedLocationDrawer || enableMapViewHideOptionFF;
  const uiMessagingData = useLagoon('ui-messaging')();
  const addToCompareText = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ADD_TO_COMPARE_TEXT,
  })?.message;
  const compareXNowLinkText = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_LINK_COMPARE_NOW,
  })?.message;

  useEffect(() => {
    if (indexOflastProviderSelected === -1 || lastSelectedIndex >= 0) {
      const newUIState = {
        ...uiState,
        compareProvidersFlow: {
          ...compareProvidersFlow,
          indexOflastProviderSelected: lastSelectedIndex + 1,
        },
        shareProvidersFlow: {
          ...shareProvidersFlow,
          indexOflastProviderSelected: lastSelectedIndex + 1,
        },
      };
      setUIstate(newUIState);
    }
  }, [lastSelectedIndex]);
  // end: a11y keyboard navigation

  const handleDirectionClick = (provider) => {
    if (selectLocation)
      selectLocation(
        provider.locationId,
        provider.longitude,
        provider.latitude
      );
  };

  const listRef = useRef<HTMLDivElement>(null);
  const processingClicksRef = useRef<Set<string>>(new Set());

  const scrollIntoView = (index: number) => {
    scrollWithRef(listRef, index);
    parentRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const { isCountySearch } = useContext(CountySearchContext);
  const { name, stateCode } = getGeoLocationFromStorage();

  const adobeLinkTrackEventCheckBox = (checkedState: any, index: number) => {
    adobeLinkTrackEvent({
      name: `provider${checkedState ? ':uncheck' : ':checked'}`,
      location: `body:${sectionType} results:page ${pageNumber}:position ${
        index + 1
      }`,
      type: 'checkbox',
    });
  };

  const displayOutOfCntyIndicator = (provider) => {
    const isOutOfCnty = isOutOfCounty(
      isCountySearch,
      name,
      stateCode,
      provider
    );

    return (
      countySearchAlertFlag &&
      isOutOfCnty && (
        <OutsideCountyAlert
          county={name?.split(',')[0]}
          isOutOfCounty={isOutOfCnty}
        />
      )
    );
  };
  const isNetworkAlertIndicator = providerResults.some((snackCardProvider) =>
    checkTerminationDate(
      displayTerminationDateEnabled,
      snackCardProvider?.networkEndDate
    )
  );
  const isInNetworkBadge = providerResults.some((snackCardProvider) =>
    isProviderStartDateInFuture(snackCardProvider?.networkStartDate)
  );

  const displayVirtualProvider = (provider: Provider) =>
    !provider.address &&
    !!provider?.virtualCareOffered?.length &&
    (provider.providerType === ProviderType.PRACTITIONER ||
      provider.providerType === ProviderType.PROVIDER_GROUP);

  const displayDataCard = (provider, index) =>
    displayVirtualProvider(provider) ? (
      <NationalGroupProvidersVirtualDataCard
        css={DataCardStyle}
        fromMobileListView={fromMobileListView}
        headers={headers}
        index={index}
        isLoading={isLoading}
        key={provider?.locationId}
        locationForAnalytics={locationForAnalytics}
        practitioner={provider}
        sectionType={sectionType}
      />
    ) : (
      <div
        data-auto-testid="data-card-container"
        data-testid="data-card-container"
        key={provider?.locationId}
        style={{
          width: enableMapViewHideOption ? window.innerWidth - 32 : '',
        }}
      >
        <DataCardContainer
          addSelectedProvider={addSelectedProvider}
          adobeLinkTrackEventCheckBox={adobeLinkTrackEventCheckBox}
          compareShareToggle={compareShareToggle}
          css={DataCardStyle}
          currentPage={currentPage}
          data-auto-testid="data-card"
          data-testid="data-card"
          drawerContent={drawerContent}
          enableMapViewHideOption={enableMapViewHideOption}
          expandedProviderListView={expandedProviderListView}
          fromMobileListView={fromMobileListView}
          handleDirectionClick={() => handleDirectionClick(provider)}
          headers={headers}
          index={index}
          isCheckboxDisabled={isCheckboxDisabled}
          isGroupedLocationDrawer={isGroupedLocationDrawer}
          isInNetworkBadge={isInNetworkBadge}
          isLoading={isLoading}
          isMNR={isMNR}
          isNetworkAlertIndicator={isNetworkAlertIndicator}
          isTieredPlan={isTieredPlan}
          locationForAnalytics={locationForAnalytics}
          map={map}
          mobileRouteView={mobileRouteView}
          navigateToDirections={navigateToDirections}
          pageNumber={pageNumber}
          practitioner={provider}
          providerList={providerResults}
          recommendationPreferences={recommendationPreferences}
          scrollIntoView={scrollIntoView}
          searchRadius={searchRadius}
          searchTerm={searchTerm}
          sectionType={sectionType}
          selectedCheckbox={selectedCheckbox}
          selectedFilters={selectedFilters}
          setIsCompareClicked={setIsCompareClicked}
          setLastSelectedIndex={setLastSelectedIndex}
          setNavigateToDirections={setNavigateToDirections}
          setRouteEndCoords={setRouteEndCoords}
          setSelectedCheckbox={setSelectedCheckbox}
          updatePin={updatePin}
          usedAt={usedAt}
        />
        {displayOutOfCntyIndicator(provider)}
        {enableMapViewHideOption && (
          <Divider
            color="$neutralGray2"
            margin={expandedProviderListView ? '0' : undefined}
          />
        )}
      </div>
    );

  const checkboxStyles = () => {
    let css = {};
    if (
      enableMapViewHideOption &&
      !expandedProviderListView &&
      !isGroupedLocationDrawer
    ) {
      css = {
        '& > div': {
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'normal !important',
        },
        '.abyss-checkbox-container': {
          marginLeft: '20px',
          '.abyss-icon-symbol': { marginLeft: '0px' },
        },
      };
    }

    return css;
  };
  const isCheckboxDisabled = (provider: any, selectedCheckbox: any) =>
    (!selectedCheckbox.checked[provider?.locationId] &&
      checkedCount >= MAX_COMPARISON_ITEMS) ||
    !provider?.latitude;

  const handleCheckboxChange = (provider: any, index: number) => {
    if (isCheckboxDisabled(provider, selectedCheckbox)) return;

    if (processingClicksRef.current.has(provider?.locationId)) return;

    processingClicksRef.current.add(provider?.locationId);
    setSelectedCheckbox((prevState) => ({
      checked: {
        ...prevState.checked,
        [provider?.locationId]: !prevState.checked[provider?.locationId],
      },
    }));
    addSelectedProvider(provider);
    adobeLinkTrackEventCheckBox(
      selectedCheckbox.checked[provider?.locationId],
      index
    );
    setLastSelectedIndex(index);
    setTimeout(() => {
      processingClicksRef.current.delete(provider?.locationId);
    }, 100);
  };

  const handleCheckboxFocus = (event: React.FocusEvent<HTMLElement>) => {
    const element = event.currentTarget;
    const isKeyboardFocus = event.target.matches(':focus-visible');

    if (element && isKeyboardFocus) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  };

  const getCompareCheckboxAriaLabel = (provider: any) => {
    const parts = ['Add to compare'];

    // Provider name
    if (provider?.providerName) {
      parts.push(provider.providerName);
    }

    // Specialty
    if (provider?.speciality) {
      parts.push(provider.speciality);
    }

    // Smart choice score
    if (provider?.recommendationDetails?.totalRecommendationScore) {
      parts.push(`Smart choice ${provider.recommendationDetails.totalRecommendationScore}%`);
    }

    return parts.join(' ');
  };

  return (
    <DataCardListStyled
      css={{
        gap:
          expandedProviderListView || isGroupedLocationDrawer ? '0px' : '16px',
        ...(!expandedProviderListView &&
          !isGroupedLocationDrawer &&
          enableMapViewHideOption && {
            paddingRight: '8px',
            marginLeft: '-5px',
            '@media (max-width: 400px)': {
              paddingRight: '16px',
            },
          }),
        '@screen < $md': {
          display: enableMapViewHideOption
            ? 'flex'
            : enableMapViewEnhancements
            ? 'grid'
            : 'flex',
        },
        '@screen < $sm': { hideScrollbar },
      }}
      data-auto-testid="data-card-list-flex"
      data-testid="data-card-list-flex"
      ref={listRef}
      variant={isMobileMap ? 'flexDirectionRow' : undefined}
    >
      {!expandedProviderListView &&
        !isGroupedLocationDrawer &&
        enableMapViewHideOption && (
          <div style={{ marginRight: '0', width: '100%' }}>
            <Divider color="$gray3" />
          </div>
        )}
      {(expandedProviderListView || isGroupedLocationDrawer) &&
        providerResults.map((provider: any, index) =>
          displayDataCard(provider, index)
        )}
      {!expandedProviderListView &&
        !isGroupedLocationDrawer &&
        providerResults.map((provider: any, index) => {
          const checkboxLabelId = `compare-checkbox-label-${provider?.locationId}`;
          return showCheckboxes ? (
            <Checkbox
              css={{
                ...DataCardCheckBoxStyle,
                'abyss-checkbox-root': {
                  ...DataCardCheckBoxStyle['abyss-checkbox-root'],
                  ...checkboxStyles(),
                },
              }}
              data-auto-testid="map-view-checkbox-group"
              data-testid="map-view-checkbox-group"
              isChecked={
                selectedCheckbox.checked[provider?.locationId] || false
              }
              isDisabled={isCheckboxDisabled(provider, selectedCheckbox)}
              key={provider?.locationId}
              label={
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <span
                    id={checkboxLabelId}
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      margin: '-1px',
                      overflow: 'hidden',
                      clip: 'rect(0,0,0,0)',
                      whiteSpace: 'nowrap',
                      border: 0
                    }}
                  >
                    {getCompareCheckboxAriaLabel(provider)}
                  </span>
                  <React.Fragment>
                    {enableMapViewHideOption &&
                      !expandedProviderListView &&
                      enableCompareSmartChoice &&
                      !isGroupedLocationDrawer && (
                        <Flex css={{ ...AddToCompareLabelStyle }} aria-hidden="true">
                          {Object.values(selectedCheckbox?.checked)?.filter(
                            (item) => item
                          )?.length >= 2 &&
                          Object.keys(selectedCheckbox.checked)
                            ?.filter((item) => selectedCheckbox?.checked[item])
                            .includes(provider?.locationId) ? (
                            <Link
                              css={{
                                '&:hover::before': { display: 'none' },
                                marginLeft: '2px',
                                fontSize: '14px',
                              }}
                              data-testid="compare-now-button"
                              onClick={setIsCompareClicked}
                            >
                              {compareXNowLinkText?.replace(
                                '{{X}}',
                                Object.values(
                                  selectedCheckbox?.checked
                                )?.filter((item) => item)?.length
                              )}
                            </Link>
                          ) : (
                            <Text css={{ fontSize: 14 }}>
                              {addToCompareText}
                            </Text>
                          )}
                        </Flex>
                      )}
                    <div aria-hidden="true">
                      <DataCardContainer
                        css={CheckBoxDataCardStyle}
                        currentPage={currentPage}
                        drawerContent={drawerContent}
                        enableMapViewHideOption={enableMapViewHideOption}
                        expandedProviderListView={expandedProviderListView}
                        fromMobileListView={fromMobileListView}
                        handleDirectionClick={() =>
                          handleDirectionClick(provider)
                        }
                        headers={headers}
                        index={index}
                        isGroupedLocationDrawer={isGroupedLocationDrawer}
                        isLoading={isLoading}
                        isMNR={isMNR}
                        isTieredPlan={isTieredPlan}
                        key={provider?.locationId}
                        locationForAnalytics={locationForAnalytics}
                        pageNumber={pageNumber}
                        practitioner={provider}
                        recommendationPreferences={recommendationPreferences}
                        scrollIntoView={scrollIntoView}
                        searchRadius={searchRadius}
                        searchTerm={searchTerm}
                        sectionType={sectionType}
                        selectedFilters={selectedFilters}
                      />
                      <div style={{ marginRight: '0', width: '100%' }}>
                        <Divider color="$gray3" />
                      </div>
                      {displayOutOfCntyIndicator(provider)}
                    </div>
                  </React.Fragment>
                </div>
              }
              onChange={() => {
                requestAnimationFrame(() => {
                  handleCheckboxChange(provider, index);
                });
              }}
              onFocus={handleCheckboxFocus}
              size={18}
            />
          ) : (
            displayDataCard(provider, index)
          );
        })}
    </DataCardListStyled>
  );
};
